/**
 * usePerformanceMonitor.ts
 * Hook React pour remplacer le setInterval dans AdvancedPerformanceMonitor
 * Utilise useEffect + refs pour gérer le monitoring périodique
 */
import { useEffect, useRef } from 'react';
import { AdvancedPerformanceMonitor } from '../utils/AdvancedPerformanceMonitor';
import { logger } from '../utils/ProfessionalLogger';

/**
 * Hook pour démarrer le monitoring de performance
 * Remplace le setInterval dans AdvancedPerformanceMonitor
 */
export function usePerformanceMonitor(enabled: boolean = true): void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const monitorRef = useRef<AdvancedPerformanceMonitor | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) return;

    const monitor = AdvancedPerformanceMonitor.getInstance();
    monitorRef.current = monitor;

    // Monitoring des frames avec requestAnimationFrame (remplace startFrameMonitoring)
    // Note: Le monitoring des frames est géré par requestAnimationFrame dans AdvancedPerformanceMonitor
    // On ne le duplique pas ici pour éviter la duplication.
    // P2.7 : `measureFrame` n'était jamais défini → ReferenceError au mount.
    // Mesure simple FPS conservée pour debug si besoin futur.
    const measureFrame = (timestamp: number) => {
      if (lastFrameTimeRef.current === 0) lastFrameTimeRef.current = timestamp;
      frameCountRef.current += 1;
      rafRef.current = requestAnimationFrame(measureFrame);
    };
    rafRef.current = requestAnimationFrame(measureFrame);

    // Monitoring périodique des métriques (remplace setInterval)
    const scheduleMonitoring = () => {
      // Nettoyer le timeout précédent
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Programmer la prochaine mise à jour
      timeoutRef.current = setTimeout(() => {
        try {
          monitor.updateMetrics();
          monitor.analyzePerformance();
          // Réprogrammer pour la prochaine fois
          scheduleMonitoring();
        } catch (error) {
          logger.error('Erreur lors du monitoring de performance:', error);
          // Réessayer après un délai en cas d'erreur
          scheduleMonitoring();
        }
      }, 1000); // 1 seconde
    };

    // Démarrer le cycle de monitoring
    scheduleMonitoring();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastFrameTimeRef.current = 0;
      frameCountRef.current = 0;
    };
  }, [enabled]);
}
